import {ClickCoordinates} from '@packages/shared/types/annotations';
import {type ProjectDataReadyForSending} from '../types';
import {
  CANVAS_SCROLL_SIZE_UPDATED,
  CANVAS_USER_SCROLL_ENDED,
  CANVAS_USER_SCROLL_STARTED,
  CANVAS_SCROLL_POSITION_UPDATED,
  PAGINATION_PAGE_SWITCHED,
  CANVAS_APP_NODE_HOVERED,
  CANVAS_APP_NODE_CLICKED,
  CUSTOM_CODE_ERROR_LOADING_SCRIPT_REPORTED_IN_CANVAS_APP,
  CANVAS_APP_IFRAME_LOADED_STATE_CHANGED,
} from '@packages/systems/designer/actions';
import {
  CANVAS_VIEWPORT_CHANGE_ENDED,
  CANVAS_VIEWPORT_CHANGE_STARTED,
  CANVAS_VIEWPORT_PANNED,
  CANVAS_VIEWPORT_ZOOMED,
} from '@packages/systems/open-canvas/store';
import type {
  CanvasViewportEventTrigger,
  ContinuousViewportChangeActionType,
  ViewportChangeSource,
} from '@packages/systems/open-canvas/store';
import {Point} from '@packages/systems/open-canvas/core';
import {COMPONENT_CANVAS_ACTIVE_FRAME_MEASURED} from '@packages/systems/component-canvas/designer';
import {type NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  type DesignerWorkflow,
  DESIGNER_WORKFLOWS,
} from '@packages/systems/designer/workflows';
import {TelemetryEventLabel} from '@packages/systems/designer-telemetry';

// Importing directly from @packages/systems/code-libraries/designer causes issues in cypress tests
// eslint-disable-next-line webflow/module-top-level-imports
import {CODE_COMPONENT_CREATOR_PROMPT_SET} from '@packages/systems/code-libraries/designer/flux/actionTypes';

export const allowedDataTypesToReceive: Set<keyof ProjectDataReadyForSending> =
  new Set([
    'siteId',
    'components',
    'collections',
    'iframeStyleSheetHref',
    'currentPage',
    'fonts',
    'breakpoints',
    'breakpointId',
    'typeKitFonts',
    'typeKitId',
    'cssVariablesState',
    'shortName',
    'timezone',
    'databaseId',
    'publicationId',
    'googleMapsApiKey',
    'assets',
    'ixStateRawMacros',
    'slugMap',
    'jsonStyles',
    'customFonts',
    'siteBasePath',
    'recaptchaRequired',
    'recaptchaSiteKey',
    'isIndexed',
    'pageHref',
    'hrefMap',
    'betaFeatureState',
    'customCodeSiteHead',
    'customCodeSitePostBody',
    'componentForms',
    'uiNodeState',
    'canvasScrollState',
    'ix2Events',
    'ix2ActionLists',
    'isPageLoading',
    'canvasResizeState',
    'openCanvasViewport',
    'locales',
    'paginationPageNumbers',
    'items',
    'ecommPublishableKey',
    'optimizationState',
    'codeIslandStore',
    'lastPublishedDomain',
    'currentItem',
    'collectionId',
    'collectionIdToPageIdMap',
    'customNodeState',
    'currentLocale',
    'variantState',
    'workflow',
    'ecommShippingZones',
    'ecommBusinessAddress',
    'quickCheckoutEnabled',
    'ecommCurrencySettings',
    'variableCollections',
    'externalCustomCode',
    'annotationsState',
    'breakpointId',
    'isOnPrimaryLocale',
    'librariesCapabilitiesMap',
    'isUnauthorizedUserType',
    'textZoom',
    'gsapScripts',
    'interactions',
    'timelines',
    'isAnonymousCommentUserType',
    'entitlements',
    'codeComponentVersion',
    'functionsPreview',
    'siteModuleDependencies',
    'isComponentCanvasActive',
    'globalComponentIds',
    'componentLocalizations',
    'installedLibrariesTagStylesSettings',
  ] as const);

const allowedActionsToReceiveFromCanvasApp = [
  CANVAS_SCROLL_SIZE_UPDATED,
  CANVAS_USER_SCROLL_ENDED,
  CANVAS_USER_SCROLL_STARTED,
  CANVAS_SCROLL_POSITION_UPDATED,
  CANVAS_VIEWPORT_CHANGE_ENDED,
  CANVAS_VIEWPORT_CHANGE_STARTED,
  CANVAS_VIEWPORT_PANNED,
  CANVAS_VIEWPORT_ZOOMED,
  PAGINATION_PAGE_SWITCHED,
  CANVAS_APP_NODE_HOVERED,
  CANVAS_APP_NODE_CLICKED,
  COMPONENT_CANVAS_ACTIVE_FRAME_MEASURED,
  CUSTOM_CODE_ERROR_LOADING_SCRIPT_REPORTED_IN_CANVAS_APP,
  CANVAS_APP_IFRAME_LOADED_STATE_CHANGED,
  CODE_COMPONENT_CREATOR_PROMPT_SET,
] as const;

export type AllowedActionsToReceiveFromCanvasAppTypes =
  (typeof allowedActionsToReceiveFromCanvasApp)[number];

export const allowedActionsToReceiveFromCanvasAppSet = new Set(
  allowedActionsToReceiveFromCanvasApp
);

// TODO: when we have action creators for this action in the packages we can reuse them here for deriving payloads types instead.
export type AllowedActionsToReceiveFromCanvasAppPayloads = {
  [CANVAS_SCROLL_SIZE_UPDATED]: {
    scrollHeight: number;
    scrollWidth: number;
  };
  [CANVAS_SCROLL_POSITION_UPDATED]: {
    left: number;
    top: number;
  };
  [CANVAS_USER_SCROLL_STARTED]: {
    userScrollingFrom: 'scrollbar' | 'canvas';
  };
  [CANVAS_USER_SCROLL_ENDED]: undefined;
  [CANVAS_VIEWPORT_PANNED]: {dx: number; dy: number};
  [CANVAS_VIEWPORT_ZOOMED]: {zoomFactor: number; originPoint: Point};
  [CANVAS_VIEWPORT_CHANGE_STARTED]: {
    trigger: CanvasViewportEventTrigger;
    isTriggeredFromIframe: boolean;
    analytics: {actionType: ContinuousViewportChangeActionType};
  };
  [CANVAS_VIEWPORT_CHANGE_ENDED]: {
    trigger: CanvasViewportEventTrigger;
    analytics: {source: ViewportChangeSource};
  };
  [PAGINATION_PAGE_SWITCHED]: {
    wrapperNativeIdPath: string[];
    pageNumber: number;
  };
  [CANVAS_APP_NODE_HOVERED]: {
    nativeIdPath: string[];
  };
  [CANVAS_APP_NODE_CLICKED]: {
    nativeIdPath: NativeId[];
    lastClickCoordinates: ClickCoordinates;
    clickedInCustomCodeElement: boolean;
  };
  [COMPONENT_CANVAS_ACTIVE_FRAME_MEASURED]: {
    width: number;
    height: number;
  };
  [CUSTOM_CODE_ERROR_LOADING_SCRIPT_REPORTED_IN_CANVAS_APP]: {
    src: string;
  };
  [CANVAS_APP_IFRAME_LOADED_STATE_CHANGED]: {
    isLoaded: boolean;
  };
  [CODE_COMPONENT_CREATOR_PROMPT_SET]: string;
};

export const CUSTOM_CANVAS_APP_NATIVE_EVENTS = {
  ERROR_LOADING_SCRIPT_IN_CUSTOM_CODE: 'errorLoadingScriptInCustomCode',
};

export const CANVAS_APP_STARTUP_LATENCY_MAP: Partial<
  Record<DesignerWorkflow, TelemetryEventLabel>
> = {
  [DESIGNER_WORKFLOWS.PREVIEW]:
    TelemetryEventLabel.CANVAS_APP_STARTUP_PREVIEW_LATENCY,
  [DESIGNER_WORKFLOWS.COMMENT]:
    TelemetryEventLabel.CANVAS_APP_STARTUP_COMMENT_LATENCY,
} as const;

export const CANVAS_APP_SWITCH_LATENCY_MAP: Partial<
  Record<DesignerWorkflow, TelemetryEventLabel>
> = {
  [DESIGNER_WORKFLOWS.PREVIEW]:
    TelemetryEventLabel.CANVAS_APP_SWITCH_PREVIEW_LATENCY,
  [DESIGNER_WORKFLOWS.COMMENT]:
    TelemetryEventLabel.CANVAS_APP_SWITCH_COMMENT_LATENCY,
} as const;
